import React from "react";
import PropTypes from "prop-types";
import { Button, TextField, Typography, Chip } from "@orijinworks/frontend-commons";
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch } from "@material-ui/core";
import { COLORS, SPACING } from "../constants";

const CHIP_COLOR_MAP = {
  direct: "primary",
  inherited: "secondary",
  active: "success",
  suspended: "default",
  inactive: "error",
  tag: "primary",
};

export const Badge = ({ type, children }) => {
  const color = CHIP_COLOR_MAP[type] || "default";
  const isInherited = type === "inherited";
  return (
    <Chip
      label={children}
      color={color}
      size="small"
      variant="outlined"
      sx={{
        borderRadius: '12px',
        fontSize: '0.7rem',
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        cursor: 'default',
        '&:hover': { backgroundColor: 'inherit' },
        '&:focus': { backgroundColor: 'inherit' },
        ...(isInherited && {
          backgroundColor: COLORS.inheritedLt,
          borderColor: COLORS.inherited,
          '&:hover': { backgroundColor: COLORS.inheritedLt },
          '&:focus': { backgroundColor: COLORS.inheritedLt },
        }),
      }}
    />
  );
};

export const Btn = ({ children, variant = "contained", onClick, disabled, sx, size }) => (
  <Button
    variant={variant}
    color="primary"
    size={size || "medium"}
    onClick={onClick}
    disabled={disabled}
    sx={sx}
  >
    {children}
  </Button>
);

export const Inp = ({ label, value, onChange, disabled, placeholder, sx, multiline, rows }) => (
  <div style={{ marginBottom: SPACING.formGroupGap, ...sx }}>
    <TextField
      label={label}
      value={value || ""}
      onChange={(event) => onChange?.(event.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      fullWidth
      variant="outlined"
      size="small"
      multiline={multiline}
      rows={rows}
    />
  </div>
);

export const Sel = ({ label, value, options, onChange, disabled, sx }) => (
  <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: SPACING.formGroupGap, ...sx }}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value ?? ""}
      onChange={(event) => onChange?.(event.target.value)}
      label={label}
      disabled={disabled}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const Toggle = ({ label, checked, onChange, disabled, sx }) => (
  <div style={{ padding: "12px 0", ...sx }}>
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={() => onChange?.()}
          disabled={disabled}
          color="primary"
        />
      }
      label={label}
      labelPlacement="start"
      style={{ marginLeft: 0 }}
    />
  </div>
);

Badge.propTypes = { type: PropTypes.string, children: PropTypes.node };
Badge.defaultProps = { type: "default", children: null };

Btn.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  size: PropTypes.string,
};
Btn.defaultProps = {
  children: null,
  variant: "contained",
  onClick: undefined,
  disabled: false,
  sx: undefined,
  size: undefined,
};

Inp.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
};
Inp.defaultProps = {
  label: undefined,
  value: "",
  onChange: undefined,
  disabled: false,
  placeholder: undefined,
  sx: undefined,
  multiline: false,
  rows: undefined,
};

Sel.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};
Sel.defaultProps = {
  label: undefined,
  value: "",
  options: [],
  onChange: undefined,
  disabled: false,
  sx: undefined,
};

Toggle.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};
Toggle.defaultProps = {
  label: undefined,
  checked: false,
  onChange: undefined,
  disabled: false,
  sx: undefined,
};

export const SectionHeading = ({ children, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: `${SPACING.sectionGap}px 0 14px`,
      paddingBottom: 10,
      borderBottom: `2px solid ${COLORS.primaryLight}`,
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        color: COLORS.headerText,
        fontSize: "0.8rem",
        fontWeight: 700,
        letterSpacing: 1.4,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
    {right}
  </div>
);

SectionHeading.propTypes = { children: PropTypes.node, right: PropTypes.node };
SectionHeading.defaultProps = { children: null, right: null };
